import type { IScope } from "angular";
import { isEqual, map, remove } from "lodash";
import { finalize } from "rxjs/operators";

import type {
    EquipmentClass,
    FunctionalityTypeInformation,
    IFunctionality,
    IFunctionalityInsert,
    IMap,
    IResponseWithLoading,
    ISite,
    ISiteEquipmentWithCoverages,
    IVideoStream,
    IViewCoverage,
} from "App/Modules/Models";
import {
    EquipmentClassFunctions,
    FunctionalityType,
    FunctionalityTypeFunctions,
    StreamSplitType,
    StreamSplitTypeFunctions,
} from "App/Modules/Models";
import type { ISiteEquipmentRepository } from "App/Modules/Repositories";
import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class MapConfigurationsComponentController {
    public static $inject: string[] = ["$scope", "mmSiteEquipmentRepository", "mmModalService"];

    public functionalityTypes: FunctionalityTypeInformation[];

    public streamSplitTypes: Array<{
        icon: string;
        name: string;
        type: StreamSplitType;
    }>;

    public map: IMap;

    public siteEquipment: ISiteEquipmentWithCoverages;

    public site: ISite;

    public isEditable: boolean;

    public isSidebarCollapsed: boolean;

    public canAddFunctionality = false;

    public removeFromMapResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    public deleteViewCoverageResponse: IResponseWithLoading<void>;

    // used in html template, don't remove!
    private aspectRatios: string[] = ["Gui.Common.NoneSelected", "3:4", "4:3", "9:16", "16:9"];

    constructor(
        private readonly $scope: IScope,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly modalService: IModalService,
    ) {}

    public $onInit() {
        this.functionalityTypes = FunctionalityTypeFunctions.getTypesWithInformation();

        const ssts: StreamSplitType[] = [
            StreamSplitType.NoSplit,
            StreamSplitType.TopHorisontalSplit,
            StreamSplitType.BottomHorisontalSplit,
            StreamSplitType.LeftVerticalSplit,
            StreamSplitType.RightVerticalSplit,
            StreamSplitType.TopLeftSplit,
            StreamSplitType.TopRightSplit,
            StreamSplitType.BottomLeftSplit,
            StreamSplitType.BottomRightSplit,
        ];

        this.streamSplitTypes = map(ssts, (sst) => ({
            icon: StreamSplitTypeFunctions.streamSplitTypeIcon(sst),
            name: StreamSplitType[sst],
            type: sst,
        }));

        this.$scope.$watch("$ctrl.isSidebarCollapsed", (newValue, oldValue) => {
            if (newValue === oldValue) {
                return;
            }

            this.$scope.$broadcast("slider.resize");
        });
    }

    public parseAspectRatio(currentAspectRatio: number, aspectRatio: string) {
        if (aspectRatio === "Gui.Common.NoneSelected") {
            return currentAspectRatio;
        }

        // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
        const numbers = aspectRatio.match(/(\d+):(\d+)/);
        return parseFloat(numbers[1]) / parseFloat(numbers[2]);
    }

    public removeSiteEquipmentFromMap() {
        this.removeFromMapResponse = { isLoading: true };
        extendWithLoadingAndFail(
            this.siteEquipmentRepository.patch(this.site.id, this.siteEquipment.id, {
                positionOnMap: null,
                rotationOnMap: null,
                equipmentElevation: null,
            } as ISiteEquipmentWithCoverages),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                    this.removeFromMapResponse = response;
                    this.siteEquipment.positionOnMap = null;
                    this.siteEquipment.rotationOnMap = null;
                    this.siteEquipment.equipmentElevation = null;
                },
                (error) => {
                    this.removeFromMapResponse = error;
                },
            );
    }

    public deleteViewCoverage(viewCoverage: IViewCoverage) {
        extendWithLoadingAndFail(
            this.siteEquipmentRepository.deleteViewCoverage(this.site.id, this.siteEquipment.id, viewCoverage.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<void>) => {
                    this.deleteViewCoverageResponse = response;
                    remove(this.siteEquipment.viewCoverages, (vc) => vc.id === viewCoverage.id);
                },
                (error) => {
                    this.deleteViewCoverageResponse = error;
                },
            );
    }

    public addFunctionality(type: FunctionalityType, viewCoverage: IViewCoverage) {
        const newFunctionality = {
            siteEquipmentId: this.siteEquipment.id,
            type,
            viewCoverageId: viewCoverage.id,
        } as IFunctionalityInsert;

        newFunctionality.type = FunctionalityType[newFunctionality.type as any as keyof typeof FunctionalityType];

        this.modalService
            .open<IFunctionalityInsert, IViewCoverage, IFunctionality>(
                "mmAddFunctionality",
                newFunctionality,
                viewCoverage,
            )
            .subscribe(
                (addedFunctionality: IFunctionality) => {
                    let videoStream: IVideoStream = null;

                    if (this.siteEquipment.videoStreams !== null && this.siteEquipment.videoStreams.length > 0) {
                        videoStream = this.siteEquipment.videoStreams[0];
                        addedFunctionality.videoStream = videoStream;
                    }

                    addedFunctionality.viewCoverage = viewCoverage;

                    viewCoverage.functionalities.push(addedFunctionality);
                    viewCoverage.siteEquipment.functionalities.push(addedFunctionality);

                    this.editFunctionality(addedFunctionality, viewCoverage);
                },
                (reason) => reason,
            );
    }

    public getAddableFunctionalityCategories(viewCoverage: IViewCoverage) {
        return Array.from(new Set(this.getAddableFunctionalityTypes(viewCoverage).map((ft) => ft.categoryName)));
    }

    public getAddableFunctionalityTypes(viewCoverage: IViewCoverage, categoryName?: string) {
        return this.functionalityTypes.filter(
            (ft) =>
                (categoryName === undefined || ft.categoryName === categoryName) &&
                !viewCoverage.functionalities.find((vcFt) => vcFt.type === ft.type),
        );
    }

    public showDirectionAndElevation(equipmentClass: EquipmentClass) {
        return EquipmentClassFunctions.showCoveragesForClass(equipmentClass);
    }

    public setStreamSplitType(viewCoverage: IViewCoverage, streamSplitType: StreamSplitType) {
        viewCoverage.streamSplitType = streamSplitType;
    }

    private editFunctionality(functionality: IFunctionality, viewCoverage: IViewCoverage) {
        let component = "";
        switch (functionality.type) {
            case FunctionalityType.MotionDetection:
                component = "irEditMotionDetectionFunctionality";
                break;
            case FunctionalityType.Recording:
                component = "irEditRecordingFunctionality";
                break;
            case FunctionalityType.FlameDetection:
                component = "irEditFlameDetectionFunctionality";
                break;
            case FunctionalityType.Care:
                component = "irEditCareFunctionality";
                break;
            default:
                throw new Error("Functionlity type not supported");
        }
        this.modalService
            .open<[IFunctionality, string, boolean], ISiteEquipmentWithCoverages, IFunctionality>(
                component,
                [functionality, this.site.id, this.isEditable],
                this.siteEquipment,
                "lg",
            )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (editedFunctionality: IFunctionality) => {
                    if (editedFunctionality !== null) {
                        functionality.name = editedFunctionality.name;
                        if (!isEqual(functionality.videoStream, editedFunctionality.videoStream)) {
                            functionality.videoStream = editedFunctionality.videoStream;
                        }
                    } else {
                        viewCoverage.siteEquipment.functionalities = viewCoverage.siteEquipment.functionalities.filter(
                            (item) => item.id !== functionality.id,
                        );
                        viewCoverage.functionalities = viewCoverage.functionalities.filter(
                            (item) => item.id !== functionality.id,
                        );
                    }
                },
                (reason) => reason,
            );
    }
}
